import { useEffect, useState } from "react";

import { getPrograms, type ProgramCard } from "../lib/api";
import { haptic } from "../lib/telegram";
import PixelField from "../components/PixelField";
import ScreenHead from "../components/ScreenHead";
import ProgramTile from "../components/ProgramTile";

interface Props {
  userName: string;
  onOpen: (id: number) => void;
  onCreate: () => void;
  onOpenLibrary: () => void;
  onOpenTemplates: () => void;
}


/** Карточки-скелетоны вместо пустого экрана, пока едет список. */
function Skeleton() {
  return (
    <div className="stack">
      {[0, 1, 2].map((i) => (
        <div key={i} className="skeleton skeleton--card" />
      ))}
    </div>
  );
}

function Empty({ onCreate, onOpenTemplates }: { onCreate: () => void; onOpenTemplates: () => void }) {
  return (
    <div className="empty">
      <div className="empty__art">
        <PixelField seed={5} />
      </div>
      <p className="empty__title">Пока пусто</p>
      <p className="lead">
        Начни с готовой — для зала, для дома или для новичка. Своя таблица тоже
        подойдёт: отправь её боту, разберу и положу сюда.
      </p>
      <button className="button" onClick={onOpenTemplates}>
        Готовые программы
      </button>
      <button className="link-button" onClick={onCreate}>
        Создать свою или загрузить таблицу
      </button>
    </div>
  );
}

export default function Programs({
  userName,
  onOpen,
  onCreate,
  onOpenLibrary,
  onOpenTemplates,
}: Props) {
  const [programs, setPrograms] = useState<ProgramCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPrograms()
      .then(setPrograms)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <div className="screen">
      <ScreenHead
        eyebrow="RACK"
        aside={userName}
        seed={7}
        title={
          <>
            Мои
            <br />
            <em>программы</em>
          </>
        }
      />

      {error && <p className="lead">Не удалось загрузить: {error}</p>}
      {!error && programs === null && <Skeleton />}
      {!error && programs?.length === 0 && (
        <Empty onCreate={onCreate} onOpenTemplates={onOpenTemplates} />
      )}

      {programs && programs.length > 0 && (
        <div className="stack">
          {programs.map((p, i) => (
            <ProgramTile key={p.id} program={p} index={i} onOpen={() => onOpen(p.id)} />
          ))}
        </div>
      )}

      {/* Готовые программы — и тем, у кого своих нет, и тем, кто ищет новую. */}
      {programs !== null && programs.length > 0 && (
        <button
          className="card card--tap"
          onClick={() => {
            haptic();
            onOpenTemplates();
          }}
        >
          <span className="card__title">Готовые программы</span>
          <span className="lead" style={{ margin: 0 }}>
            Семь программ — для зала, дома и новичка. Одна кнопка — и она в календаре.
          </span>
        </button>
      )}

      {/* Справочник живёт здесь, а не в профиле: в него ходят из программ —
          посмотреть, что вообще есть, и открыть динамику по движению. */}
      <button
        className="card card--tap"
        onClick={() => {
          haptic();
          onOpenLibrary();
        }}
      >
        <span className="card__title">Библиотека упражнений</span>
        <span className="lead" style={{ margin: 0 }}>
          290 движений, свои — с характеристиками. Отсюда же открывается динамика
          по каждому.
        </span>
      </button>

      {/* Кнопка нужна только когда список есть: в пустом состоянии она уже там.
          Плавающей она была до появления таб-панели — теперь низ экрана занят
          навигацией, и висящая поверх неё кнопка перекрывала две вкладки. */}
      {programs !== null && programs.length > 0 && (
        <button
          className="button button--wide"
          onClick={() => {
            haptic();
            onCreate();
          }}
        >
          + Программа
        </button>
      )}
    </div>
  );
}
